import React, { useState } from 'react';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { Link, useNavigate, useParams } from 'react-router-dom';
import {
  ArrowLeft,
  Building2,
  ChevronLeft,
  FilePlus2,
  FileText,
  RefreshCw,
} from 'lucide-react';
import { toast } from 'react-hot-toast';

import ReportsImportDialog from '../components/Reports/ReportsImportDialog';
import { DocumentStatusBadge, RunStatusBadge } from '../components/Reports/ReportStatus';
import apiService from '../services/api';
import type { CompanyDetail } from '../types';
import { analysisProfileLabel, completionPercent, formatReportDate, reportPeriodLabel } from '../utils/reportDashboard';

const ReportsCompanyPage: React.FC = () => {
  const { companyId = '' } = useParams<{ companyId: string }>();
  const [importOpen, setImportOpen] = useState(false);
  const queryClient = useQueryClient();
  const navigate = useNavigate();

  const companyQuery = useQuery({
    queryKey: ['report-company', companyId],
    queryFn: () => apiService.getTrackedCompany(companyId),
    enabled: !!companyId,
    refetchInterval: (query) => {
      const company = query.state.data;
      return company?.reports.some((report) => report.document_status === 'processing' || report.document_status === 'uploaded')
        ? 5000
        : false;
    },
  });

  if (companyQuery.isLoading) {
    return (
      <div className="mx-auto max-w-7xl px-4 py-8 sm:px-6 lg:px-8" aria-label="טוען חברה">
        <div className="h-9 w-64 animate-pulse rounded bg-gray-200 motion-reduce:animate-none" />
        <div className="mt-8 space-y-3">
          {[0, 1].map((item) => <div key={item} className="h-28 animate-pulse rounded-2xl bg-white motion-reduce:animate-none" />)}
        </div>
      </div>
    );
  }

  if (companyQuery.isError || !companyQuery.data) {
    return (
      <div className="mx-auto max-w-3xl px-4 py-20 text-center">
        <Building2 className="mx-auto h-10 w-10 text-gray-300" />
        <h1 className="mt-4 text-lg font-semibold text-gray-900">החברה לא נמצאה או שאינה זמינה עבורך</h1>
        <Link to="/reports" className="mt-5 inline-flex min-h-11 items-center gap-2 rounded-lg px-4 text-sm font-medium text-primary-700 hover:bg-primary-50">
          חזרה לחברות
        </Link>
      </div>
    );
  }

  const company: CompanyDetail = companyQuery.data;

  return (
    <div className="mx-auto w-full max-w-7xl px-4 py-7 sm:px-6 sm:py-9 lg:px-8">
      <nav className="mb-5 flex items-center gap-1 text-xs text-gray-500" aria-label="פירורי לחם">
        <Link to="/reports" className="flex min-h-11 items-center rounded-lg px-2 hover:bg-white hover:text-primary-700">חברות</Link>
        <ChevronLeft className="h-3.5 w-3.5" />
        <span className="truncate text-gray-700">{company.name}</span>
      </nav>

      <header className="flex flex-col gap-5 border-b border-gray-200 pb-6 sm:flex-row sm:items-end sm:justify-between">
        <div className="flex min-w-0 items-start gap-4">
          <span className="flex h-12 w-12 flex-shrink-0 items-center justify-center rounded-2xl bg-primary-50 text-primary-700">
            <Building2 className="h-6 w-6" />
          </span>
          <div className="min-w-0">
            <h1 className="truncate text-2xl font-semibold tracking-tight text-gray-950 sm:text-3xl">{company.name}</h1>
            <p className="mt-2 text-sm text-gray-500">
              {analysisProfileLabel(company.analysis_profile)}
              {company.sector ? ` · ${company.sector}` : ''}
              {company.tase_issuer_id ? ` · מזהה מנפיק ${company.tase_issuer_id}` : ''}
            </p>
          </div>
        </div>
        <button
          type="button"
          onClick={() => setImportOpen(true)}
          className="inline-flex min-h-11 items-center justify-center gap-2 rounded-xl bg-primary-600 px-4 text-sm font-semibold text-white hover:bg-primary-700"
        >
          <FilePlus2 className="h-4 w-4" /> דוח נוסף
        </button>
      </header>

      <section className="mt-7" aria-labelledby="company-reports-title">
        <div className="mb-3 flex items-end justify-between gap-4">
          <div>
            <h2 id="company-reports-title" className="text-sm font-semibold text-gray-900">ציר זמן דוחות</h2>
            <p className="mt-1 text-xs text-gray-500">{company.reports.length} דוחות זמינים לצפייה ולחילוץ.</p>
          </div>
          {companyQuery.isFetching && <RefreshCw className="h-4 w-4 animate-spin text-gray-400" aria-label="מעדכן" />}
        </div>

        {company.reports.length === 0 ? (
          <div className="rounded-2xl border border-dashed border-gray-300 bg-white py-14 text-center">
            <FileText className="mx-auto h-9 w-9 text-gray-300" />
            <p className="mt-3 text-sm text-gray-600">אין עדיין דוחות לחברה.</p>
          </div>
        ) : (
          <div className="overflow-hidden rounded-2xl border border-gray-200 bg-white">
            <ul className="divide-y divide-gray-100">
              {company.reports.map((report) => {
                const run = report.latest_run;
                const percent = completionPercent(run?.stats);
                return (
                  <li key={report.id}>
                    <Link
                      to={`/reports/${report.id}`}
                      className="group grid gap-4 px-4 py-5 transition-colors hover:bg-gray-50 focus:outline-none focus-visible:ring-2 focus-visible:ring-inset focus-visible:ring-primary-500 sm:grid-cols-[minmax(220px,1.2fr)_minmax(190px,1fr)_minmax(220px,1fr)_44px] sm:items-center sm:px-5"
                    >
                      <div className="flex min-w-0 items-center gap-3">
                        <span className="flex h-10 w-10 flex-shrink-0 items-center justify-center rounded-xl bg-gray-100 text-gray-600">
                          <FileText className="h-5 w-5" />
                        </span>
                        <div className="min-w-0">
                          <h3 className="text-sm font-semibold text-gray-950">{reportPeriodLabel(report)}</h3>
                          <p className="mt-1 truncate text-xs text-gray-500" title={report.document_name}>{report.document_name}</p>
                          <p className="mt-1 text-[11px] text-gray-400">נוסף {formatReportDate(report.created_at)}{report.page_count ? ` · ${report.page_count} עמודים` : ''}</p>
                        </div>
                      </div>

                      <div>
                        <div className="text-xs font-medium text-gray-500">מצב מסמך</div>
                        <div className="mt-2"><DocumentStatusBadge status={report.document_status} progress={report.processing_progress} /></div>
                        {report.processing_stage && report.document_status !== 'processed' && (
                          <p className="mt-1.5 line-clamp-2 text-[11px] text-gray-500">{report.processing_stage}</p>
                        )}
                      </div>

                      <div>
                        <div className="flex items-center justify-between gap-2">
                          <span className="text-xs font-medium text-gray-500">ריצת קורפוס</span>
                          {run && <RunStatusBadge status={run.status} />}
                        </div>
                        {run ? (
                          <div className="mt-3 flex items-center gap-3">
                            <div className="h-2 flex-1 overflow-hidden rounded-full bg-gray-100">
                              <div className="h-full rounded-full bg-primary-600" style={{ width: `${percent}%` }} />
                            </div>
                            <span className="w-9 text-left text-xs font-semibold tabular-nums text-gray-700">{percent}%</span>
                          </div>
                        ) : (
                          <p className="mt-2 text-sm text-gray-600">
                            {report.document_status === 'processed' ? 'מוכן להפעלת חילוץ' : 'החילוץ יתאפשר לאחר העיבוד'}
                          </p>
                        )}
                      </div>

                      <span className="flex h-11 w-11 items-center justify-center rounded-lg text-gray-400 transition group-hover:bg-white group-hover:text-primary-700">
                        <ArrowLeft className="h-5 w-5" />
                      </span>
                    </Link>
                  </li>
                );
              })}
            </ul>
          </div>
        )}
      </section>

      <ReportsImportDialog
        open={importOpen}
        initialCompanyName={company.name}
        initialAnalysisProfile={company.analysis_profile}
        onClose={() => setImportOpen(false)}
        onCreated={async (report) => {
          setImportOpen(false);
          toast.success('הדוח נוסף למעקב והעיבוד החל');
          await Promise.all([
            queryClient.invalidateQueries({ queryKey: ['report-company', companyId] }),
            queryClient.invalidateQueries({ queryKey: ['report-companies'] }),
          ]);
          if (report.company_id !== companyId) navigate(`/reports/companies/${report.company_id}`);
        }}
      />
    </div>
  );
};

export default ReportsCompanyPage;
